'use client';

import * as React from 'react';
import { Bookmark } from 'lucide-react';

import { cn } from '@/components/atoms/primitives/utils';

export interface SaveEventButtonProps {
  /** Whether this event is currently saved by the authenticated user. */
  isSaved: boolean;
  /**
   * Visible event title — used for the screen-reader label so list users
   * hear `"Save Demo Concert"` instead of a bare `"Save"`.
   */
  eventTitle: string;
  /**
   * Fired when an authenticated user clicks the button. Receives the next
   * saved-state so the parent can dispatch the appropriate mutation.
   */
  onToggle: (nextSaved: boolean) => void;
  /**
   * Fired when an anonymous user clicks the button. The parent is responsible
   * for redirecting to signup with a `?action=save` deep link (or whatever
   * auth surface it chooses). Must be supplied — silent failure on anonymous
   * save is explicitly disallowed by FRE-803 acceptance criteria.
   */
  onRequireAuth: () => void;
  /**
   * Whether a user is authenticated. When `false`, clicks call `onRequireAuth`
   * instead of `onToggle`.
   */
  isAuthenticated: boolean;
  /** Optional class merged onto the button. */
  className?: string;
  /**
   * Disable user interaction while the parent mutation is in flight. The
   * button stays focusable for screen readers but `onToggle` won't fire.
   */
  disabled?: boolean;
}

/**
 * SaveEventButton — the canonical save affordance for event surfaces (Discover
 * list/map, category cards, etc).
 *
 * Behavior:
 *   - Authenticated → fires `onToggle(nextSaved)`. The parent owns the API
 *     call + optimistic update + cache invalidation.
 *   - Anonymous → fires `onRequireAuth()`. No silent failure (FRE-803 AC).
 *
 * The button is designed to overlay parent rows/cards that are themselves
 * navigable `<a>` elements. It calls `stopPropagation()` + `preventDefault()`
 * on click so the parent link does not navigate when the user toggles the
 * saved state.
 *
 * Hit target is 44×44 to satisfy WCAG 2.2 minimum target size.
 */
export function SaveEventButton({
  isSaved,
  eventTitle,
  onToggle,
  onRequireAuth,
  isAuthenticated,
  className,
  disabled = false,
}: SaveEventButtonProps): React.ReactElement {
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the parent <a> row from navigating to the event detail when
      // the user clicks the save toggle. preventDefault for completeness;
      // stopPropagation so the click never reaches the row link's onClick.
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      if (!isAuthenticated) {
        onRequireAuth();
        return;
      }

      onToggle(!isSaved);
    },
    [disabled, isAuthenticated, isSaved, onRequireAuth, onToggle],
  );

  const label = isSaved ? `Unsave ${eventTitle}` : `Save ${eventTitle}`;

  return (
    <button
      type="button"
      data-testid="save-event-button"
      data-saved={isSaved ? 'true' : 'false'}
      aria-label={label}
      aria-pressed={isSaved}
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center rounded-full',
        'h-11 desktop:h-9 w-11 desktop:w-9 min-h-[44px] desktop:min-h-0 min-w-[44px] desktop:min-w-0',
        'bg-background/80 backdrop-blur-sm',
        'text-loci-text hover:bg-background/95',
        'transition-colors motion-safe:duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline-glow',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    >
      <Bookmark
        className={cn('size-5', isSaved && 'fill-current text-clt-ember')}
        aria-hidden="true"
      />
    </button>
  );
}
